import { Account, Positions, useAccountStore } from '@/store/use-account-store';
import {
  FilledTransfer,
  NewTransfer,
  Transfer,
  useTransfersStore,
} from '@/store/use-transfers-store';
import {
  CapitalStatus,
  OrderStatus,
  OrderType,
  TransferType,
} from '@/types/enums';
import { formatBigInt, parseDecimalToBigInt } from '@/utils/value-format';

import { TransferResponse } from '@/features/account/api/get-transfers';

import {
  FilledOrder,
  Order,
  PartiallyFilledOrder,
  useOrdersStore,
} from '@/store/use-orders-store';

import { OrderResponse } from '@/features/account/api/get-orders';
import {
  getAvailableFunds,
  getPositionFunding,
  getPositionInitMargin,
  getPositionUnrealizedPnL,
  getPositionValue,
  getUpdatedAccountOnFill,
} from '@/features/account/utils/math';
import { getFee } from '@/features/trade/order/utils/math';
import {
  getIsStatusEnd,
  getIsStatusFundedEnd,
  getIsStatusPreEval,
} from '@/features/vest-capital/utils/status';
import { getPrivyPrimaryAddress } from '@/hooks';
import { queryClient } from '@/lib/api-clients/query-client';
import { CapitalStatusResponse } from '@/lib/api/get-capital-status';
import { getDefaultBigDecimal, logConsole } from '@/lib/utils';
import {
  COLLATERAL_DECIMALS,
  CUM_FUNDING_DECIMALS,
  getAllMarketStoreStates,
  MarketSpec,
  useMarketStore,
} from '@/store/use-markets-store';
import { getIsMigrationCompleted } from '@/store/use-migration-store';
import { useModalStore } from '@/store/use-modal-store';
import { AccountResponse } from '@/types';
import { User } from '@privy-io/react-auth';
import { toast } from 'sonner';
import { getByNormalizedKey } from './helpers';

export const transferResponseToTransfer = (res: TransferResponse): Transfer => {
  if (res.status === OrderStatus.REJECTED) return res;
  return {
    ...res,
    size: parseDecimalToBigInt(res.size, COLLATERAL_DECIMALS),
  };
};

export const handleTransferForStore = (
  transfer: Transfer,
  options?: {
    address?: string;
    skipAccountUpdate?: boolean;
  },
) => {
  const {
    accounts,
    address: storeAddress,
    setAccount,
  } = useAccountStore.getState();

  const address = options?.address ?? storeAddress;
  if (!address) return;

  const { setTransfer, addNewDeposit, getTransfers } =
    useTransfersStore.getState();

  const transfers = getTransfers(address);
  const existing =
    transfer.id in transfers &&
    transfers[transfer.id].status === transfer.status;

  setTransfer(transfer.id, transfer, address);

  if (
    options?.skipAccountUpdate ||
    existing ||
    transfer.status === OrderStatus.REJECTED
  )
    return;

  logConsole(
    false,
    `[Transfer Handler]: ${transfer.status} transfer existing`,
    existing,
  );

  const account = accounts[address];
  if (!account || !account.hasHydrated) return;

  if (
    transfer.orderType === TransferType.DEPOSIT &&
    transfer.status === OrderStatus.FILLED
  ) {
    const filledDeposit = transfer as FilledTransfer;
    addNewDeposit(filledDeposit, address);

    logConsole(false, '[Fill Handler]: Deposit update time', {
      account: account.lastUpdated,
      transfer: filledDeposit.postTime,
    });

    if (filledDeposit.postTime >= account.lastUpdated) {
      setAccount(
        {
          collateral: account.collateral + filledDeposit.size,
        },
        filledDeposit.postTime,
        address,
      );
      useAccountStore.getState().updateAccount({ address });
    }
  } else if (
    transfer.orderType === TransferType.WITHDRAW ||
    transfer.orderType === TransferType.FUNDED_PROFIT_WITHDRAW
  ) {
    switch (transfer.status) {
      case OrderStatus.NEW:
        const newWithdraw = transfer as NewTransfer;

        logConsole(false, '[Fill Handler]: Withdraw update time', {
          account: account.lastUpdated,
          transfer: newWithdraw.postTime,
        });

        if (newWithdraw.postTime >= account.lastUpdated) {
          setAccount(
            {
              collateral: account.collateral - newWithdraw.size,
            },
            newWithdraw.postTime,
            address,
          );
          useAccountStore.getState().updateAccount({ address });
        }
        break;
      case OrderStatus.FILLED:
        const filledTransfer = transfer as FilledTransfer;
        queryClient.invalidateQueries({
          queryKey: ['wallet/balance', filledTransfer.chainId],
        });
        toast.success(
          `Successfully withdrew ${formatBigInt(
            filledTransfer.size,
            COLLATERAL_DECIMALS,
            {
              digits: 2,
              round: 'ceil',
            },
          )} USD.`,
        );
        break;

      case OrderStatus.VERIFIED:
        toast.dismiss();
        useModalStore.getState().setVerifiedTransfer(transfer);
        useModalStore.setState({ isWithdrawConfirmationModalOpen: true });
        break;
    }
  }
};

export const handleAccountForStore = (
  res: AccountResponse,
  forcedAddress?: string,
) => {
  const {
    accounts,
    setAccount,
    address: storeAddress,
  } = useAccountStore.getState();

  const address = forcedAddress ?? storeAddress;
  if (!address) return;
  const lastUpdated = accounts[address]?.lastUpdated ?? 0;
  if (res.time <= lastUpdated) {
    setAccount({ hasHydrated: true }, lastUpdated, address);
  } else {
    const positions = res.positions.reduce((acc, pos) => {
      const { marketSpec, setMarketData, marketData } = useMarketStore(
        pos.symbol,
      ).getState();

      const markPrice = parseDecimalToBigInt(
        pos.markPrice,
        marketSpec.priceDecimals,
      );
      const indexPrice = parseDecimalToBigInt(
        pos.indexPrice,
        marketSpec.priceDecimals,
      );

      setMarketData({
        indexPrice,
        markPrice,
        ...(marketData.midPrice ? {} : { midPrice: markPrice }),
      });

      const size = parseDecimalToBigInt(pos.size, marketSpec.sizeDecimals);
      const entryFunding = parseDecimalToBigInt(
        pos.entryFunding,
        CUM_FUNDING_DECIMALS,
      );
      const initMarginRatio = parseDecimalToBigInt(pos.initMarginRatio, 4n);
      const cost =
        parseDecimalToBigInt(pos.entryPrice, marketSpec.priceDecimals) * size;

      const fee = getFee(pos.symbol, size, marketData.midPrice || markPrice, {
        address,
      });
      const funding = getPositionFunding(
        pos.symbol,
        pos.isLong,
        size,
        entryFunding,
      );
      const initMargin = getPositionInitMargin(
        pos.symbol,
        cost,
        initMarginRatio,
      );

      const value = getPositionValue(pos.symbol, size);
      const unrealizedPnl = getPositionUnrealizedPnL(pos.symbol, {
        isLong: pos.isLong,
        size,
        cost,
        initMargin,
      });

      acc[pos.symbol] = {
        isLong: pos.isLong,
        size,
        cost,
        entryFunding,
        initMarginRatio,
        unrealizedPnl,
        value,
        liqPrice: getDefaultBigDecimal(marketSpec.priceDecimals),
        fee,
        funding,
        initMargin,
      };
      return acc;
    }, {} as Positions);

    const account: Omit<Account, 'lastUpdated'> = {
      availableFunds: getDefaultBigDecimal(),
      value: getDefaultBigDecimal(),
      unrealizedPnl: getDefaultBigDecimal(),
      leverage: getDefaultBigDecimal(2),
      collateral: parseDecimalToBigInt(res.collateral, COLLATERAL_DECIMALS),
      maintMarginReq: getDefaultBigDecimal(),
      positions: getIsMigrationCompleted() ? {} : positions,
      connections: {
        twitter: res.twitterUsername,
        discord: res.discordUsername,
      },
      withdrawable: parseDecimalToBigInt(res.withdrawable, COLLATERAL_DECIMALS),
      nonWithdrawable: parseDecimalToBigInt(
        res.nonWithdrawable,
        COLLATERAL_DECIMALS,
      ),
      hasHydrated: true,
    };

    setAccount(account, res.time, address);
    // NOTE: invoke getState again for updateAccount to have most recent store data
    useAccountStore.getState().updateAccount({ address });

    res.leverages.forEach((lvg) =>
      useMarketStore(lvg.symbol).setState({ leverage: lvg.value }),
    );
  }
};

/**
 * Reduces any TP/SL orders' size tied to the position.
 */
const handleReduceOnlyForStore = (
  order: FilledOrder | PartiallyFilledOrder,
  address?: string,
) => {
  if (!order.reduceOnly) return;

  const { address: storeAddress, accounts } = useAccountStore.getState();
  const resolvedAddress = address ?? storeAddress;
  if (!resolvedAddress) return;

  const account = getByNormalizedKey(accounts, resolvedAddress);
  if (!account) return;

  const position = account.positions[order.symbol];
  if (!position) return;

  useOrdersStore.setState((state) => {
    const store = state.byWallet[resolvedAddress];
    const ids = Array.from(store.newOrderIds).filter((id) => {
      const tpslOrder = store.orders[id];
      if (tpslOrder.symbol !== order.symbol) return false;
      if (
        tpslOrder.orderType !== OrderType.TAKE_PROFIT &&
        tpslOrder.orderType !== OrderType.STOP_LOSS
      )
        return false;
      if (tpslOrder.size <= position.size) return false;
      return true;
    });
    if (ids.length > 0) {
      logConsole(
        false,
        `[Fill Handler]: Adjusting ${ids.length} TP/SL orders' size...`,
      );
    }
    const orders = ids.reduce(
      (prev, id) => ({
        ...prev,
        [id]: { ...store.orders[id], size: position.size },
      }),
      {} as Record<string, Order>,
    );
    return {
      ...state,
      byWallet: {
        ...state.byWallet,
        [resolvedAddress]: {
          ...store,
          orders: {
            ...store.orders,
            ...orders,
          },
        },
      },
    };
  });
};

const handlePartialFillForStore = (
  order: PartiallyFilledOrder,
  address?: string,
) => {
  const { address: storeAddress } = useAccountStore.getState();

  const resolvedAddress = address ?? storeAddress;

  if (!resolvedAddress) return;

  const { newOrderIds } = useOrdersStore.getState().getOrders(resolvedAddress);

  if (newOrderIds.has(order.parentId)) {
    useOrdersStore.setState((state) => {
      const store = { ...state.byWallet[resolvedAddress] };

      store.orders[order.parentId].size -= order.lastFilledSize;

      if (store.orders[order.parentId].size <= 0n) {
        store.newOrderIds.delete(order.parentId);
      }
      return {
        byWallet: {
          ...state.byWallet,
          [resolvedAddress]: store,
        },
      };
    });
  }
};

const handleFillForStore = (
  order: FilledOrder | PartiallyFilledOrder,
  options?: { address: string; cumFunding?: bigint },
) => {
  const {
    address: storeAddress,
    accounts,
    setAccount,
  } = useAccountStore.getState();

  const address = options?.address ?? storeAddress;
  if (!address) return;

  const { getOrders, setOrder } = useOrdersStore.getState();

  const { filledOrderIds } = getOrders(address);
  const existing = filledOrderIds.has(order.id);

  logConsole(false, '[Fill Handler]: Order existing', existing);

  setOrder(order, address);

  if (existing) return;

  if (getIsMigrationCompleted()) return;

  const account = accounts[address];
  if (!account || !account.hasHydrated) return;

  logConsole(false, '[Fill Handler]: Update time', {
    account: account.lastUpdated,
    order: order.lastFilledTime,
  });

  if (order.lastFilledTime < account.lastUpdated) return;

  const updatedAccount = getUpdatedAccountOnFill(
    order.symbol,
    order.isBuy,
    order.lastFilledSize,
    order.lastFilledPrice,
    order.realizedPnl,
    order.initMarginRatio,
    account,
    {
      address,
      cumFunding: options?.cumFunding,
    },
  );

  setAccount(updatedAccount, order.lastFilledTime, address);

  if (order.status === OrderStatus.PARTIALLY_FILLED) {
    handlePartialFillForStore(order, address);
  }

  if (order.reduceOnly) {
    handleReduceOnlyForStore(order, address);
  }
};

export const handleOrderForStore = (
  order: Order,
  options?: {
    skipFill?: boolean;
    address?: string;
    cumFunding?: bigint;
  },
) => {
  const { address: storeAddress, accounts } = useAccountStore.getState();
  const address = options?.address ?? storeAddress;
  if (!address) return;
  if (
    !options?.skipFill &&
    (order.status === OrderStatus.FILLED ||
      order.status === OrderStatus.PARTIALLY_FILLED)
  ) {
    handleFillForStore(order, { address, cumFunding: options?.cumFunding });
  } else if (
    order.status === OrderStatus.NEW &&
    (order.orderType === OrderType.TAKE_PROFIT ||
      order.orderType === OrderType.STOP_LOSS)
  ) {
    const account = getByNormalizedKey(accounts, address);
    if (!account) return;
    const position = account.positions[order.symbol];
    if (!position) return;
    useOrdersStore.getState().setOrder(
      {
        ...order,
        size: order.size > position.size ? position.size : order.size,
      },
      address,
    );
  } else {
    useOrdersStore.getState().setOrder(order, address);
  }
  if (
    !order.reduceOnly &&
    ((!options?.skipFill && order.status === OrderStatus.CANCELLED) ||
      order.orderType === OrderType.LIMIT)
  ) {
    const availableFunds = getAvailableFunds({ address });
    useAccountStore
      .getState()
      .updateAccount({ address, account: { availableFunds } });
  }
};

// export const handleOrderForStore = (orderResponse: OrderResponse) => {
//   const { setOrder } = useOrdersStore.getState();
//   const order = orderResponseToOrder(orderResponse);
//   setOrder(order);
//   return order;
// };

export const orderResponseToOrder = (orderResponse: OrderResponse): Order => {
  let marketSpec: MarketSpec | undefined;
  if (orderResponse.symbol) {
    const markets = getAllMarketStoreStates();
    if (orderResponse.symbol in markets) {
      marketSpec = markets[orderResponse.symbol].marketSpec;
    }
  }
  switch (orderResponse.status) {
    case OrderStatus.NEW:
      return {
        ...orderResponse,
        size: marketSpec
          ? parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals)
          : 0n,
        limitPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.limitPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice:
          orderResponse.tpPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.tpPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        slPrice:
          orderResponse.slPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.slPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
      };
    case OrderStatus.FILLED:
    case OrderStatus.PARTIALLY_FILLED:
      return {
        ...orderResponse,
        size: marketSpec
          ? parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals)
          : 0n,
        limitPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.limitPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice:
          orderResponse.tpPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.tpPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        slPrice:
          orderResponse.slPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.slPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        lastFilledSize: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.lastFilledSize,
              marketSpec.sizeDecimals,
            )
          : 0n,
        lastFilledPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.lastFilledPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        avgFilledPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.avgFilledPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        realizedPnl: parseDecimalToBigInt(
          orderResponse.realizedPnl,
          COLLATERAL_DECIMALS,
        ),
        settledFunding: parseDecimalToBigInt(
          orderResponse.settledFunding,
          COLLATERAL_DECIMALS,
        ),
        fees: parseDecimalToBigInt(orderResponse.fees, COLLATERAL_DECIMALS),
        markPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.markPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
      };
    case OrderStatus.CANCELLED:
      return {
        ...orderResponse,
        size: marketSpec
          ? parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals)
          : 0n,
        limitPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.limitPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice:
          orderResponse.tpPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.tpPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        slPrice:
          orderResponse.slPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.slPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
      };
    case OrderStatus.REJECTED:
      return {
        ...orderResponse,
        size: marketSpec
          ? parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals)
          : 0n,
        limitPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.limitPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice:
          orderResponse.tpPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.tpPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        slPrice:
          orderResponse.slPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.slPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
      };
  }
};

export const handleCapitalStatusForStore = (
  res: CapitalStatusResponse,
  address?: string,
) => {
  const { getCapitalStatus, setCapitalStatus } = useAccountStore.getState();
  for (const addr in res) {
    const oldStatus = getCapitalStatus(addr);
    const newCapitalStatus = res[addr].capital_status;
    switch (oldStatus) {
      case CapitalStatus.PRE_FUNDED:
        if (
          newCapitalStatus === CapitalStatus.FUNDED ||
          getIsStatusFundedEnd(newCapitalStatus)
        ) {
          setCapitalStatus(newCapitalStatus, addr);
        } else {
          logConsole(false, 'Skipping pre-funded status change: ', {
            address: addr,
            newCapitalStatus,
          });
        }
        break;
      case CapitalStatus.PRE_EVAL:
      case CapitalStatus.PENDING_PAYMENT:
        if (!getIsStatusPreEval(newCapitalStatus)) {
          setCapitalStatus(newCapitalStatus, addr);
        }
        break;
      default:
        if (
          address?.toLowerCase() === addr &&
          getIsStatusEnd(newCapitalStatus)
        ) {
          useModalStore.setState({ isCapitalResultModalOpen: true });
        }
        // NOTE: check for preventing the case where FE queries status for funded account that hasn't yet been updated
        if (
          oldStatus !== CapitalStatus.FUNDED ||
          newCapitalStatus === CapitalStatus.FUNDED_BLOCKED ||
          getIsStatusFundedEnd(newCapitalStatus)
        ) {
          setCapitalStatus(newCapitalStatus, addr);
        }
        break;
    }
  }
};

export const handleUserForStore = (user: User) => {
  const { address, setAddress } = useAccountStore.getState();
  if (!!address) return;
  const primaryAddress = getPrivyPrimaryAddress(user);
  if (!primaryAddress) return;
  setAddress(primaryAddress);
};
